import ListItem from "@rentiohq/shared-frontend/dist/components/components/ListItem";
import ListItemSeparator from "@rentiohq/shared-frontend/dist/components/components/ListItemSeparator";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import {
  getLocale,
  getLocalizedText,
} from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatPhone } from "@rentiohq/shared-frontend/dist/utils/phone/phone.utils";
import { IUser } from "@rentiohq/shared/dist/types/auth.types";
import { ELocale } from "@rentiohq/shared/dist/types/i18n.types";
import React from "react";
import * as S from "./ProfileMenu.styles";
import { TProps } from "./ProfileMenu.types";

enum EMenu {
  Profile = "profile",
  Language = "language",
}

interface IState {
  menuToShow?: EMenu;
}

class ProfileMenu extends React.Component<TProps, IState> {
  private wrap: React.ReactNode | null | undefined;

  // Constructor
  constructor(props: TProps) {
    super(props);

    this.state = {
      menuToShow: undefined,
    };
  }

  // Lifecycle methods
  public componentDidMount() {
    document.addEventListener("mousedown", this.handleMousedown);
  }

  public componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleMousedown);
  }

  // Render
  public render = () => {
    const { user, hasAccessToken } = this.props;

    return (
      <div ref={ref => (this.wrap = ref)}>
        <S.Wrap>
          <S.MenuWrap>
            <S.MenuButton onPress={this.handlePressToggleMenu(EMenu.Language)}>
              <S.LanguageIcon />
              <S.MenuButtonTitle>
                {getLocalizedText("system.locale.short_key")}
              </S.MenuButtonTitle>
            </S.MenuButton>
            {this.renderLanguageMenu()}
          </S.MenuWrap>

          {user && hasAccessToken && (
            <S.MenuWrap>
              <S.MenuButton onPress={this.handlePressToggleMenu(EMenu.Profile)}>
                <S.ProfileIcon />
              </S.MenuButton>
              {this.renderProfileMenu(user)}
            </S.MenuWrap>
          )}
        </S.Wrap>
      </div>
    );
  };

  private renderLanguageMenu = () => {
    if (!this.state.menuToShow || this.state.menuToShow !== EMenu.Language) {
      return;
    }

    return (
      <S.Menu>
        <S.MenuHeader>
          {getLocalizedText("system.locale").toUpperCase()}
        </S.MenuHeader>
        <ListItemSeparator />
        {[ELocale.NL, ELocale.FR, ELocale.EN].map((locale, index) => (
          <React.Fragment key={locale}>
            {index > 0 && <ListItemSeparator />}
            <ListItem
              title={getLocalizedText(`system.locale.${locale}`)}
              onPress={this.handlePressChangeLocale(locale)}
              isActive={locale === getLocale()}
            />
          </React.Fragment>
        ))}
        {this.renderInternalLocaleListItem()}
      </S.Menu>
    );
  };

  private renderInternalLocaleListItem = () => {
    const { user } = this.props;

    if (!(user?.personalContact?.email || "").includes("@rentio.be")) {
      return null;
    }

    return (
      <>
        <ListItemSeparator />
        <ListItem
          title={"Keys"}
          onPress={this.handlePressChangeLocale(ELocale.INTERNAL)}
          isActive={ELocale.INTERNAL === getLocale()}
        />
      </>
    );
  };

  private renderProfileMenu = (user: IUser) => {
    if (!this.state.menuToShow || this.state.menuToShow !== EMenu.Profile) {
      return;
    }

    return (
      <S.Menu>
        <S.MenuHeader>{getName(user).toUpperCase()}</S.MenuHeader>
        <ListItemSeparator />
        <S.MenuHeaderInfo>
          {user.phone ? formatPhone(user.phone) : "No Phone"}
          {"\n"}
          {user.email}
        </S.MenuHeaderInfo>
        <ListItemSeparator />
        <ListItem
          title={getLocalizedText("nav.profile_edit")}
          onPress={this.handlePressEditProfile}
        />
        <ListItemSeparator />
        <ListItem
          title={getLocalizedText("nav.logout")}
          onPress={this.handlePressSignOut}
        />
      </S.Menu>
    );
  };

  // Event handlers
  private handleMousedown = (event: any) => {
    // @ts-ignore
    if (this.wrap && !this.wrap.contains(event.target)) {
      this.setState({
        menuToShow: undefined,
      });
    }
  };

  private handlePressToggleMenu = (menuToShow: EMenu) => () => {
    this.setState(prevState => {
      if (prevState.menuToShow !== menuToShow) {
        return { menuToShow };
      }

      return { menuToShow: prevState.menuToShow ? undefined : menuToShow };
    });
  };

  private handlePressChangeLocale = (locale: ELocale) => async () => {
    await this.closeMenu();

    this.props.setLocale(locale);
  };

  private handlePressEditProfile = async () => {
    await this.closeMenu();

    const url = "https://rentio.page.link/install-the-app";
    window.open(url, "_blank");
  };

  private handlePressSignOut = async () => {
    await this.closeMenu();

    this.props.signOut();
  };

  // Helpers
  private closeMenu = () =>
    new Promise(resolve => {
      this.setState(
        {
          menuToShow: undefined,
        },
        () => {
          resolve(true);
        },
      );
    });
}

export default ProfileMenu;
